<template>
  <div v-if="theme !== 'standard'" class="fixed inset-0 pointer-events-none z-0 overflow-hidden">

    <!-- Space Mission -->
    <template v-if="theme === 'space-mission'">
      <div
        v-for="star in stars"
        :key="'star-' + star.id"
        class="absolute rounded-full bg-white twinkle-star"
        :style="{
          left: star.x + '%',
          top: star.y + '%',
          width: star.size + 'px',
          height: star.size + 'px',
          animationDelay: star.delay + 's',
          animationDuration: star.duration + 's'
        }"
      />
      <svg class="absolute inset-0 w-full h-full">
        <defs>
          <radialGradient v-for="n in nebulae" :key="'ng-' + n.id" :id="'bg-neb-' + n.id" cx="50%" cy="50%" r="50%">
            <stop offset="0%" :stop-color="n.color" :stop-opacity="n.opacity" />
            <stop offset="100%" :stop-color="n.color" stop-opacity="0" />
          </radialGradient>
        </defs>
        <ellipse
          v-for="n in nebulae"
          :key="'ne-' + n.id"
          :cx="n.x + '%'" :cy="n.y + '%'"
          :rx="n.rx" :ry="n.ry"
          :fill="`url(#bg-neb-${n.id})`"
        />
      </svg>
    </template>

    <!-- Mountain Trail -->
    <template v-else-if="theme === 'mountain-trail'">
      <svg class="absolute inset-0 w-full h-full" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice">
        <!-- Pine trees scattered along edges -->
        <g v-for="tree in trailTrees" :key="'tt-' + tree.id"
          :transform="`translate(${tree.x}, ${tree.y}) scale(${tree.scale})`"
          :opacity="tree.opacity">
          <polygon points="0,-20 -8,0 8,0" :fill="tree.color" />
          <polygon points="0,-14 -6,3 6,3" :fill="tree.colorLight" />
          <rect x="-1.5" y="0" width="3" height="6" fill="#92400e" rx="1" />
        </g>
        <!-- Clouds with drift animation -->
        <g v-for="cloud in trailClouds" :key="'tc-' + cloud.id"
          :opacity="cloud.opacity" class="cloud-drift"
          :style="{ animationDelay: cloud.delay + 's', animationDuration: cloud.duration + 's' }">
          <ellipse :cx="cloud.x" :cy="cloud.y" rx="28" ry="9" fill="#ede9e0" />
          <ellipse :cx="cloud.x + 18" :cy="cloud.y - 5" rx="20" ry="8" fill="#ede9e0" />
          <ellipse :cx="cloud.x - 14" :cy="cloud.y - 2" rx="16" ry="7" fill="#ede9e0" />
        </g>
        <!-- Cow -->
        <g transform="translate(760, 680) scale(1)" opacity="0.18">
          <!-- body -->
          <ellipse cx="0" cy="0" rx="14" ry="9" fill="#f5f5f4" />
          <!-- spots -->
          <ellipse cx="-4" cy="-2" rx="4" ry="3" fill="#78716c" />
          <ellipse cx="5" cy="1" rx="3" ry="2.5" fill="#78716c" />
          <!-- head -->
          <circle cx="14" cy="-4" r="5" fill="#f5f5f4" />
          <!-- ears -->
          <ellipse cx="17" cy="-8" rx="2" ry="1.5" fill="#d6d3d1" />
          <ellipse cx="11" cy="-8" rx="2" ry="1.5" fill="#d6d3d1" />
          <!-- eyes -->
          <circle cx="15.5" cy="-5" r="0.8" fill="#1c1917" />
          <circle cx="12.5" cy="-5" r="0.8" fill="#1c1917" />
          <!-- nose -->
          <ellipse cx="16" cy="-2" rx="2.5" ry="1.5" fill="#e7e5e4" />
          <!-- legs -->
          <line x1="-8" y1="9" x2="-8" y2="16" stroke="#a8a29e" stroke-width="2" />
          <line x1="-3" y1="9" x2="-3" y2="16" stroke="#a8a29e" stroke-width="2" />
          <line x1="4" y1="9" x2="4" y2="16" stroke="#a8a29e" stroke-width="2" />
          <line x1="9" y1="9" x2="9" y2="16" stroke="#a8a29e" stroke-width="2" />
          <!-- tail -->
          <path d="M-14,0 Q-20,-6 -18,-12" stroke="#a8a29e" stroke-width="1.5" fill="none" />
        </g>
      </svg>
      <!-- Mountain silhouette along bottom -->
      <div class="absolute bottom-0 left-0 right-0 h-[25vh] overflow-hidden">
        <svg class="w-full h-full" viewBox="0 0 1000 200" preserveAspectRatio="none">
          <polygon points="0,200 50,120 120,155 220,80 300,140 420,55 550,110 650,70 750,130 850,90 950,145 1000,200"
            fill="#d6d3d1" opacity="0.10" />
          <polygon points="0,200 80,145 180,170 280,110 380,155 500,95 600,135 700,90 820,140 920,120 1000,200"
            fill="#a8a29e" opacity="0.07" />
        </svg>
      </div>
    </template>

    <!-- Road Map -->
    <template v-else-if="theme === 'road-map'">
      <svg class="absolute inset-0 w-full h-full" viewBox="0 0 800 800" preserveAspectRatio="xMidYMid slice">
        <!-- Grass patches along edges -->
        <ellipse v-for="g in roadGrass" :key="'rg-' + g.id"
          :cx="g.x" :cy="g.y"
          :rx="g.rx" :ry="g.ry"
          :fill="g.color" :opacity="g.opacity" />
        <!-- Round trees at edges -->
        <g v-for="tree in roadTrees" :key="'rt-' + tree.id"
          :transform="`translate(${tree.x}, ${tree.y}) scale(${tree.scale})`"
          :opacity="tree.opacity">
          <rect x="-2" y="0" width="4" height="10" fill="#92400e" rx="2" />
          <circle cx="0" cy="-4" r="10" :fill="tree.color" />
          <circle cx="-5" cy="-2" r="7" :fill="tree.color" opacity="0.7" />
          <circle cx="5" cy="-2" r="7" :fill="tree.color" opacity="0.7" />
        </g>
        <!-- Birds -->
        <g v-for="bird in roadBirds" :key="'rb-' + bird.id"
          :transform="`translate(${bird.x}, ${bird.y})`" :opacity="bird.opacity"
          class="bird-float" :style="{ animationDelay: bird.delay + 's' }">
          <path d="M-6,0 Q0,-6 6,0" stroke="#0d9488" stroke-width="1.5" fill="none" />
          <path d="M-3,0 Q0,-4 3,0" stroke="#14b8a6" stroke-width="1" fill="none" />
        </g>
        <!-- Cow -->
        <g transform="translate(50, 520) scale(1)" opacity="0.15">
          <ellipse cx="0" cy="0" rx="14" ry="9" fill="#fafaf9" />
          <ellipse cx="-4" cy="-2" rx="4" ry="3" fill="#a8a29e" />
          <ellipse cx="5" cy="1" rx="3" ry="2.5" fill="#a8a29e" />
          <circle cx="14" cy="-4" r="5" fill="#fafaf9" />
          <ellipse cx="17" cy="-8" rx="2" ry="1.5" fill="#d6d3d1" />
          <ellipse cx="11" cy="-8" rx="2" ry="1.5" fill="#d6d3d1" />
          <circle cx="15.5" cy="-5" r="0.8" fill="#1c1917" />
          <circle cx="12.5" cy="-5" r="0.8" fill="#1c1917" />
          <ellipse cx="16" cy="-2" rx="2.5" ry="1.5" fill="#e7e5e4" />
          <line x1="-8" y1="9" x2="-8" y2="16" stroke="#a8a29e" stroke-width="2" />
          <line x1="-3" y1="9" x2="-3" y2="16" stroke="#a8a29e" stroke-width="2" />
          <line x1="4" y1="9" x2="4" y2="16" stroke="#a8a29e" stroke-width="2" />
          <line x1="9" y1="9" x2="9" y2="16" stroke="#a8a29e" stroke-width="2" />
          <path d="M-14,0 Q-20,-6 -18,-12" stroke="#a8a29e" stroke-width="1.5" fill="none" />
        </g>
      </svg>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { usePreferencesStore } from '@/stores/preferencesStore';

const preferencesStore = usePreferencesStore();
const theme = computed(() => preferencesStore.homeTheme);

// ── Space Mission decorations ──
const stars = computed(() => {
  const result = [];
  for (let i = 0; i < 40; i++) {
    const seed = i * 31 + 7;
    result.push({
      id: i,
      x: (seed * 17) % 100,
      y: (seed * 53 + i * 41) % 100,
      size: 1 + (seed % 3),
      delay: (seed % 50) / 10,
      duration: 2 + (seed % 30) / 10
    });
  }
  return result;
});

const nebulae = computed(() => [
  { id: 0, x: 10, y: 15, rx: 120, ry: 60, color: '#6366f1', opacity: 0.18 },
  { id: 1, x: 85, y: 50, rx: 100, ry: 50, color: '#8b5cf6', opacity: 0.14 },
  { id: 2, x: 50, y: 80, rx: 130, ry: 65, color: '#ec4899', opacity: 0.12 },
  { id: 3, x: 20, y: 92, rx: 90, ry: 45, color: '#06b6d4', opacity: 0.15 },
]);

// ── Mountain Trail decorations ──
// Positions in SVG viewBox coords (800x800)
const trailTrees = computed(() => [
  { id: 0, x: 20,  y: 120, scale: 1.0, opacity: 0.16, color: '#16a34a', colorLight: '#22c55e' },
  { id: 1, x: 775, y: 260, scale: 0.8, opacity: 0.14, color: '#15803d', colorLight: '#16a34a' },
  { id: 2, x: 15,  y: 400, scale: 1.1, opacity: 0.18, color: '#22c55e', colorLight: '#4ade80' },
  { id: 3, x: 780, y: 520, scale: 0.7, opacity: 0.12, color: '#16a34a', colorLight: '#22c55e' },
  { id: 4, x: 25,  y: 650, scale: 0.9, opacity: 0.16, color: '#15803d', colorLight: '#16a34a' },
]);

const trailClouds = computed(() => [
  { id: 0, x: 150, y: 50,  opacity: 0.14, delay: 0,  duration: 30 },
  { id: 1, x: 600, y: 120, opacity: 0.10, delay: 8,  duration: 35 },
  { id: 2, x: 350, y: 30,  opacity: 0.08, delay: 15, duration: 28 },
]);

// ── Road Map decorations ──
// Positions in SVG viewBox coords (800x800)
const roadGrass = computed(() => {
  const result = [];
  const positions = [
    { x: 40, y: 100 }, { x: 740, y: 200 }, { x: 50, y: 350 },
    { x: 720, y: 480 }, { x: 60, y: 600 }, { x: 750, y: 700 },
    { x: 30, y: 750 },
  ];
  for (let i = 0; i < positions.length; i++) {
    const seed = i * 13 + 7;
    result.push({
      id: i,
      x: positions[i].x,
      y: positions[i].y,
      rx: 20 + (seed % 15),
      ry: 8 + (seed % 6),
      color: ['#86efac', '#bbf7d0', '#dcfce7'][seed % 3],
      opacity: 0.12 + (seed % 3) * 0.03
    });
  }
  return result;
});

const roadTrees = computed(() => [
  { id: 0, x: 20,  y: 160, scale: 0.7, opacity: 0.16, color: '#16a34a' },
  { id: 1, x: 780, y: 350, scale: 0.6, opacity: 0.14, color: '#22c55e' },
  { id: 2, x: 15,  y: 520, scale: 0.7, opacity: 0.16, color: '#4ade80' },
  { id: 3, x: 785, y: 700, scale: 0.5, opacity: 0.12, color: '#16a34a' },
]);

const roadBirds = computed(() => [
  { id: 0, x: 680, y: 60,  opacity: 0.18, delay: 0 },
  { id: 1, x: 120, y: 250, opacity: 0.12, delay: 2 },
]);
</script>

<style scoped>
.twinkle-star {
  animation: twinkle ease-in-out infinite alternate;
}

@keyframes twinkle {
  0% { opacity: 0.15; transform: scale(0.8); }
  100% { opacity: 0.7; transform: scale(1.3); }
}

.cloud-drift {
  animation: drift linear infinite;
}

@keyframes drift {
  0% { transform: translateX(0); }
  50% { transform: translateX(15px); }
  100% { transform: translateX(0); }
}

.bird-float {
  animation: birdFloat 4s ease-in-out infinite;
}

@keyframes birdFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}
</style>
